import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ActivityMonitoringCommandCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [timeRange, setTimeRange] = useState('24h');

  const liveActivityFeed = [
    { id: 1, type: 'login', user: 'john.doe@company.com', action: 'Successful login', severity: 'info', timestamp: '2 seconds ago', ip: '192.168.1.100', location: 'New York, US' },
    { id: 2, type: 'security', user: 'unknown', action: 'Failed login attempt (3rd)', severity: 'warning', timestamp: '15 seconds ago', ip: '45.123.67.89', location: 'Unknown' },
    { id: 3, type: 'system', user: 'system', action: 'Database backup completed', severity: 'success', timestamp: '1 minute ago', ip: 'internal', location: 'Server' },
    { id: 4, type: 'data', user: 'jane.smith@company.com', action: 'Exported client data', severity: 'info', timestamp: '2 minutes ago', ip: '192.168.1.105', location: 'London, UK' },
    { id: 5, type: 'security', user: 'admin@company.com', action: 'Permission changed for user', severity: 'warning', timestamp: '3 minutes ago', ip: '192.168.1.1', location: 'New York, US' },
    { id: 6, type: 'error', user: 'system', action: 'API endpoint timeout', severity: 'critical', timestamp: '5 minutes ago', ip: 'internal', location: 'Server' },
    { id: 7, type: 'login', user: 'mike.johnson@company.com', action: 'Successful login', severity: 'info', timestamp: '8 minutes ago', ip: '192.168.1.110', location: 'Toronto, CA' },
    { id: 8, type: 'data', user: 'sarah.williams@company.com', action: 'Bulk data update', severity: 'info', timestamp: '10 minutes ago', ip: '192.168.1.115', location: 'Sydney, AU' }
  ];

  const securityMetrics = [
    { metric: 'Failed Login Attempts', value: 23, change: -15.3, trend: 'down', status: 'good', last24h: 23 },
    { metric: 'Suspicious Activities', value: 7, change: 12.5, trend: 'up', status: 'warning', last24h: 7 },
    { metric: 'Data Access Logs', value: 1847, change: 8.7, trend: 'up', status: 'normal', last24h: 1847 },
    { metric: 'Security Alerts', value: 12, change: -22.1, trend: 'down', status: 'good', last24h: 12 }
  ];

  const performanceMetrics = [
    { metric: 'Server Response Time', value: '245ms', status: 'excellent', threshold: '< 500ms', current: 245, max: 500 },
    { metric: 'Database Query Time', value: '89ms', status: 'excellent', threshold: '< 200ms', current: 89, max: 200 },
    { metric: 'API Endpoint Uptime', value: '99.8%', status: 'excellent', threshold: '> 99.5%', current: 99.8, max: 100 },
    { metric: 'Error Rate', value: '0.3%', status: 'excellent', threshold: '< 1%', current: 0.3, max: 1 },
    { metric: 'Active Connections', value: '847', status: 'good', threshold: '< 1000', current: 847, max: 1000 },
    { metric: 'Memory Usage', value: '68%', status: 'good', threshold: '< 80%', current: 68, max: 80 }
  ];

  const userActivityStats = [
    { period: 'Last Hour', logins: 234, actions: 1847, sessions: 456, avgDuration: '12m 34s' },
    { period: 'Last 24 Hours', logins: 3456, actions: 28934, sessions: 7234, avgDuration: '15m 42s' },
    { period: 'Last 7 Days', logins: 18923, actions: 156789, sessions: 42156, avgDuration: '14m 18s' },
    { period: 'Last 30 Days', logins: 67845, actions: 589234, sessions: 156234, avgDuration: '13m 56s' }
  ];

  const geographicDistribution = [
    { country: 'United States', users: 3456, percentage: 42, sessions: 12456 },
    { country: 'United Kingdom', users: 1847, percentage: 22, sessions: 6789 },
    { country: 'Canada', users: 1234, percentage: 15, sessions: 4567 },
    { country: 'Australia', users: 892, percentage: 11, sessions: 3234 },
    { country: 'Germany', users: 567, percentage: 7, sessions: 2123 },
    { country: 'Others', users: 234, percentage: 3, sessions: 891 }
  ];

  const recentAlerts = [
    { id: 1, type: 'security', title: 'Multiple failed login attempts', severity: 'high', time: '5 minutes ago', status: 'active' },
    { id: 2, type: 'performance', title: 'API response time exceeded threshold', severity: 'medium', time: '15 minutes ago', status: 'resolved' },
    { id: 3, type: 'system', title: 'Database connection pool near limit', severity: 'medium', time: '1 hour ago', status: 'active' },
    { id: 4, type: 'security', title: 'Unusual data access pattern detected', severity: 'high', time: '2 hours ago', status: 'investigating' }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-300';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'success': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      default: return 'bg-blue-100 text-blue-700 border-blue-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-100 text-emerald-700';
      case 'good': return 'bg-blue-100 text-blue-700';
      case 'warning': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'login': return 'LogIn';
      case 'security': return 'Shield';
      case 'system': return 'Server';
      case 'data': return 'Database';
      case 'error': return 'AlertTriangle';
      default: return 'Activity';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar isCollapsed={sidebarCollapsed} />

      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Activity Monitoring Command Center</h1>
                <p className="text-slate-600 mt-1">Real-time system oversight and security monitoring</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-emerald-700">System Operational</span>
                </div>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e?.target?.value)}
                  className="px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <Button className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2">
                  <Icon name="Download" className="w-5 h-5" />
                  Export Logs
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-8">
          {/* Security Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {securityMetrics?.map((metric, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 text-sm font-medium">{metric?.metric}</span>
                  <Icon name="Shield" className="w-5 h-5 text-violet-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-2">{metric?.value}</div>
                <div className={`flex items-center gap-1 text-sm ${metric?.trend === 'down' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  <Icon name={metric?.trend === 'down' ? 'TrendingDown' : 'TrendingUp'} className="w-4 h-4" />
                  <span>{Math.abs(metric?.change)}% vs last period</span>
                </div>
              </div>
            ))}
          </div>

          {/* Live Activity Feed & Recent Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Live Activity Feed */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Live Activity Feed</h2>
                    <p className="text-slate-600 text-sm mt-1">Real-time event streaming</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e?.target?.value)}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="all">All Types</option>
                      <option value="login">Login</option>
                      <option value="security">Security</option>
                      <option value="system">System</option>
                      <option value="data">Data</option>
                    </select>
                    <select
                      value={filterSeverity}
                      onChange={(e) => setFilterSeverity(e?.target?.value)}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="all">All Severity</option>
                      <option value="critical">Critical</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="p-6 max-h-[600px] overflow-y-auto">
                <div className="space-y-3">
                  {liveActivityFeed?.map((activity) => (
                    <div key={activity?.id} className={`p-4 rounded-lg border ${getSeverityColor(activity?.severity)}`}>
                      <div className="flex items-start gap-3">
                        <Icon name={getTypeIcon(activity?.type)} className="w-5 h-5 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-900">{activity?.action}</span>
                            <span className="text-xs text-slate-500">{activity?.timestamp}</span>
                          </div>
                          <div className="text-sm text-slate-600 mb-2">{activity?.user}</div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Icon name="MapPin" className="w-3 h-3" />
                              {activity?.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Globe" className="w-3 h-3" />
                              {activity?.ip}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Alerts */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Recent Alerts</h2>
                <p className="text-slate-600 text-sm mt-1">Priority notifications</p>
              </div>
              <div className="p-6 space-y-4">
                {recentAlerts?.map((alert) => (
                  <div key={alert?.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Icon name="AlertTriangle" className="w-5 h-5 text-amber-600" />
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertSeverityColor(alert?.severity)}`}>
                        {alert?.severity}
                      </span>
                    </div>
                    <h3 className="font-medium text-slate-900 mb-2">{alert?.title}</h3>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{alert?.time}</span>
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">{alert?.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-white rounded-xl border border-slate-200 mb-8">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">System Performance Metrics</h2>
              <p className="text-slate-600 text-sm mt-1">Real-time performance indicators</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
              {performanceMetrics?.map((metric, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-600 text-sm font-medium">{metric?.metric}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(metric?.status)}`}>
                      {metric?.status}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-3">{metric?.value}</div>
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>Current</span>
                      <span>Threshold: {metric?.threshold}</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-violet-600 rounded-full"
                        style={{ width: `${(metric?.current / metric?.max) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Activity Statistics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">User Activity Statistics</h2>
                <p className="text-slate-600 text-sm mt-1">Login patterns and session analytics</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {userActivityStats?.map((stat, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-4">
                      <div className="font-medium text-slate-900 mb-3">{stat?.period}</div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <div className="text-xs text-slate-600 mb-1">Logins</div>
                          <div className="text-lg font-bold text-slate-900">{stat?.logins?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-600 mb-1">Actions</div>
                          <div className="text-lg font-bold text-slate-900">{stat?.actions?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-600 mb-1">Sessions</div>
                          <div className="text-lg font-bold text-slate-900">{stat?.sessions?.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-600 mb-1">Avg Duration</div>
                          <div className="text-lg font-bold text-slate-900">{stat?.avgDuration}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Geographic Distribution */}
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Geographic Distribution</h2>
                <p className="text-slate-600 text-sm mt-1">User activity by location</p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {geographicDistribution?.map((location, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-32">
                        <div className="text-sm font-medium text-slate-900">{location?.country}</div>
                        <div className="text-xs text-slate-500">{location?.users?.toLocaleString()} users</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-8 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full flex items-center justify-end px-3"
                              style={{ width: `${location?.percentage}%` }}
                            >
                              <span className="text-white text-xs font-medium">{location?.percentage}%</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 w-20 text-right">
                            <div className="text-sm font-medium text-slate-700">{location?.sessions?.toLocaleString()}</div>
                            <div className="text-xs text-slate-500">sessions</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityMonitoringCommandCenter;